import React from 'react';
import { ImageIcon, Video } from 'lucide-react';
import { LeadFormModal } from '../components/LeadFormModal';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { Breadcrumb } from '../components/Breadcrumb';
import { WhatNextSection } from '../components/WhatNextSection';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';

export const VisualGeneratorAiPage: React.FC = () => {
    usePageMetadata({
        title: "Visual Generator AI | Generator Grafik i Wideo z AI",
        description: "Twórz spójne grafiki i wideo dla social media w sekundy. Zapisz się na listę oczekujących na Visual Generator AI, aby zautomatyzować tworzenie wizualizacji.",
        schema: {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Visual Generator AI (Grafiki & Video)",
            "serviceType": "Generowanie Treści Wizualnych",
            "provider": {
                "@type": "Organization",
                "name": "BiznesBezKlikania.pl"
            },
            "description": "Narzędzie AI do automatycznego tworzenia spójnych wizualnie grafik i krótkich form wideo na potrzeby mediów społecznościowych.",
            "url": "https://biznesbezklikania.pl/rozwiazania/visual-generator-ai"
        }
    });

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[60vh] flex items-center overflow-hidden py-20 md:py-24">
                <ParticlesBackground variant="minimal" />
                <AnimatedBlob variant="purple" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 text-center relative z-10">
                    <RevealOnScroll>
                        <div className="flex justify-center items-center gap-4 mb-6">
                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
                                <ImageIcon className="w-8 h-8 text-white" />
                            </div>
                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
                                <Video className="w-8 h-8 text-white" />
                            </div>
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                            Visual Generator AI
                        </h1>
                        <p className="text-sm font-semibold text-purple-500 dark:text-purple-400 uppercase tracking-wider mb-4">
                            Grafiki & Video
                        </p>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto mb-10">
                            Automatyczne tworzenie spójnych wizualnie grafik i krótkich form wideo na potrzeby mediów społecznościowych. Produkt wkrótce w ofercie!
                        </p>
                    </RevealOnScroll>

                    <RevealOnScroll delay={200}>
                        <LeadFormModal
                            productSlug="visual-generator-ai"
                            triggerLabel="Zapisz się na listę oczekujących"
                            triggerClassName="px-8 py-4 font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-500 rounded-xl hover:shadow-xl hover:shadow-purple-500/30 hover:scale-[1.02] transition-all duration-300 text-lg inline-flex items-center group"
                        />
                    </RevealOnScroll>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            <Breadcrumb />

            {/* Coming Soon Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="dots" animated={false} />
                <div className="container mx-auto px-4 max-w-3xl relative z-10">
                    <RevealOnScroll>
                        <GlassCard variant="strong" className="text-center">
                            <h2 className="text-3xl font-bold mb-4 text-text-primary-light dark:text-text-primary-dark">
                                Wkrótce...
                            </h2>
                            <p className="text-text-secondary-light dark:text-text-secondary-dark">
                                Pracujemy nad potężnym narzędziem, które zautomatyzuje tworzenie materiałów wizualnych dla Twojej marki. Wyobraź sobie spójne, profesjonalne grafiki i wideo generowane na podstawie Twoich wytycznych w kilka sekund. Zapisz się na listę, aby otrzymać informację o premierze i specjalną zniżkę!
                            </p>
                        </GlassCard>
                    </RevealOnScroll>
                </div>
            </section>

            <WhatNextSection diyLink="/rozwiazania/visual-generator-ai" />
        </>
    );
};
